import api, { ApiResponse } from 'api';
import Button from 'components/button/Button';
import { Spinner } from 'components/spinner/Spinner';
import { endpoints } from 'endpoints.config';
import { localizedFormatDate } from 'helpers/categoryHelpers/userLocale';
import { Toast, ToastMessageReason } from 'helpers/toast';
import { useCallback, useEffect, useState } from 'react';
import { AddMerchantModal } from './AddMerchantModal';
import { MerchantCredentialsModal } from './MerchantCredentialsModal';
import { MerchantSecretAction, MerchantSecretModal } from './MerchantSecretModal';
import {
    apiErrors,
    copyToClipboard,
    ManagedMerchant,
    merchantErrorMessage,
    MerchantCredentials,
    PAYMENT_SETUP_PENDING,
} from './models';

type ShownCredentials = { merchantName: string; credentials: MerchantCredentials };
type SecretRequest = { merchant: ManagedMerchant; action: MerchantSecretAction };

const rowKey = (merchant: ManagedMerchant) =>
    merchant.widgetClientsId ? `w${merchant.widgetClientsId}` : `m${merchant.managedMerchantsId}`;

export const MerchantManagement = () => {
    const [merchants, setMerchants] = useState<ManagedMerchant[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [shownCredentials, setShownCredentials] = useState<ShownCredentials | null>(null);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [secretRequest, setSecretRequest] = useState<SecretRequest | null>(null);

    const loadMerchants = useCallback(async () => {
        try {
            const res = await api.get<ApiResponse<ManagedMerchant[]>>(endpoints.merchantManagement.list);
            setMerchants(res.data.status === '1' ? res.data.details ?? [] : []);
        } catch (ex: any) {
            Toast.openToastMessage(merchantErrorMessage(apiErrors(ex)[0]?.messageCode), ToastMessageReason.ERROR);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadMerchants();
    }, [loadMerchants]);

    const onCreated = (merchantName: string, credentials: MerchantCredentials) => {
        setShowAdd(false);
        setShownCredentials({ merchantName, credentials });
        loadMerchants();
    };

    const retry = async (merchant: ManagedMerchant) => {
        setBusyKey(rowKey(merchant));
        try {
            const res = await api.post<ApiResponse<MerchantCredentials>>(endpoints.merchantManagement.retry, {
                managedMerchantsId: merchant.managedMerchantsId,
            });
            if (res.data.status === '1' && res.data.details) {
                setShownCredentials({ merchantName: merchant.merchantName, credentials: res.data.details });
            }
        } catch (ex: any) {
            const code = apiErrors(ex)[0]?.messageCode;
            Toast.openToastMessage(
                merchantErrorMessage(code),
                code === PAYMENT_SETUP_PENDING ? ToastMessageReason.PENDING : ToastMessageReason.ERROR
            );
        } finally {
            setBusyKey(null);
            loadMerchants();
        }
    };

    const onSecretShown = (credentials: MerchantCredentials) => {
        if (!secretRequest) return;
        const { merchant, action } = secretRequest;
        setSecretRequest(null);
        setShownCredentials({ merchantName: merchant.merchantName, credentials });
        if (action === 'regenerate') loadMerchants();
    };

    const copyValue = async (label: string, value: string) => {
        const copied = await copyToClipboard(value);
        Toast.openToastMessage(
            copied ? `${label} copied` : `Could not copy the ${label.toLowerCase()}`,
            copied ? ToastMessageReason.VALID : ToastMessageReason.ERROR
        );
    };

    return (
        <div className="MerchantManagementPage">
            <div className="MerchantManagementHeader">
                <h1>Merchants</h1>
                <Button onClick={() => setShowAdd(true)}>Add merchant</Button>
            </div>

            {loading ? (
                <Spinner />
            ) : merchants.length === 0 ? (
                <p className="MerchantManagementEmpty">No merchants yet.</p>
            ) : (
                <div className="MerchantManagementTableWrapper">
                    <table className="MerchantManagementTable">
                        <thead>
                            <tr>
                                <th>Merchant</th>
                                <th>Website</th>
                                <th>Descriptor</th>
                                <th>Portal user</th>
                                <th>Status</th>
                                <th>API key</th>
                                <th>Added</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {merchants.map((merchant) => (
                                <tr key={rowKey(merchant)}>
                                    <td className="notranslate" translate="no">
                                        {merchant.merchantName}
                                    </td>
                                    <td>{merchant.domain}</td>
                                    <td className="notranslate" translate="no">
                                        {merchant.statementDescriptor}
                                    </td>
                                    <td className="notranslate" translate="no">
                                        {merchant.gatewayUsername ?? '-'}
                                        {merchant.contactEmail && (
                                            <div className="MerchantSubText">{merchant.contactEmail}</div>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`MerchantStatus ${merchant.status}`}>{merchant.status}</span>
                                        {merchant.status === 'Pending' && merchant.lastError && (
                                            <div className="MerchantLastError">{merchant.lastError}</div>
                                        )}
                                    </td>
                                    <td>
                                        {merchant.apiKey ? (
                                            <button
                                                type="button"
                                                className="MerchantApiKey notranslate"
                                                translate="no"
                                                title="Copy API key"
                                                onClick={() => copyValue('API key', merchant.apiKey as string)}
                                            >
                                                {merchant.apiKey}
                                            </button>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td>{localizedFormatDate(new Date(merchant.addDate))}</td>
                                    <td className="MerchantActions">
                                        {merchant.status === 'Pending' && merchant.managedMerchantsId ? (
                                            <Button
                                                variety="small"
                                                disabled={busyKey !== null}
                                                onClick={() => retry(merchant)}
                                            >
                                                {busyKey === rowKey(merchant) ? 'Retrying...' : 'Retry set up'}
                                            </Button>
                                        ) : merchant.widgetClientsId ? (
                                            <>
                                                <Button
                                                    variety="small"
                                                    priority="secondary"
                                                    disabled={busyKey !== null}
                                                    onClick={() => setSecretRequest({ merchant, action: 'reveal' })}
                                                >
                                                    Show secret
                                                </Button>
                                                <Button
                                                    variety="small"
                                                    priority="secondary"
                                                    disabled={busyKey !== null}
                                                    onClick={() => setSecretRequest({ merchant, action: 'regenerate' })}
                                                >
                                                    New secret
                                                </Button>
                                            </>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showAdd && (
                <AddMerchantModal
                    onClose={() => setShowAdd(false)}
                    onCreated={onCreated}
                    onSaved={() => {
                        setShowAdd(false);
                        loadMerchants();
                    }}
                />
            )}

            {secretRequest && (
                <MerchantSecretModal
                    merchant={secretRequest.merchant}
                    action={secretRequest.action}
                    onClose={() => setSecretRequest(null)}
                    onSuccess={onSecretShown}
                />
            )}

            {shownCredentials && (
                <MerchantCredentialsModal
                    merchantName={shownCredentials.merchantName}
                    credentials={shownCredentials.credentials}
                    onClose={() => setShownCredentials(null)}
                />
            )}
        </div>
    );
};
